import { Injectable, computed, inject, signal } from '@angular/core';
import { RdsBadgeColor } from '@rds/core';
import { Observable, finalize, forkJoin, of, tap } from 'rxjs';

import {
  BookingHistoryEntryModel,
  BookingRequestApiService,
  BookingRequestDetailsUpdateModel,
  BookingRequestDetailsViewModel,
  BookingRequestPageParams,
  BookingRequestSortField,
  BookingStatusApiResponseEnum,
  GoogleAnalyticsEventCategory,
  GoogleAnalyticsEventName,
  GoogleAnalyticsService,
  Order,
  PagedResponse,
  RoleType,
  RoomBookingModel,
  RoomDetailsModel,
  RoomServicesEnum,
  RoomServicesModel,
  RoomsApiService,
} from '@bookly/shared';

export interface BookingRequestFilters {
  searchPhrase: string | null;
  services?: RoomServicesEnum[];
  siteNames?: string[];
  buildingNames?: string[];
  floorNames?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class BookingRequestService {
  readonly #bookingRequestApiService = inject(BookingRequestApiService);
  readonly #roomsApiService = inject(RoomsApiService);
  readonly #googleAnalyticService = inject(GoogleAnalyticsService);

  readonly #bookingDetailsSignal =
    signal<BookingRequestDetailsViewModel | null>(null);
  readonly #bookingHistorySignal = signal<BookingHistoryEntryModel[]>([]);
  readonly #isBookingHistoryLoadingSignal = signal(false);
  readonly #allBookingsSignal = signal<RoomBookingModel[]>([]);
  readonly #isRoomBookingDataLoadingSignal = signal(false);
  readonly #isDetailsDataLoadingSignal = signal(false);
  readonly #roomDetailsSignal = signal<RoomDetailsModel | null>(null);
  readonly #roomBookingsSnapshotSignal = signal<RoomBookingModel[]>([]);
  readonly #bookingRequestFiltersSignal = signal<BookingRequestFilters>({
    searchPhrase: null,
  });
  readonly #siteNamesSignal = computed(() => {
    const rooms = this.#roomBookingsSnapshotSignal();
    if (!rooms) {
      return [];
    }
    return Array.from(
      new Set(rooms.map(room => room.siteName).filter(Boolean))
    ).sort();
  });

  readonly #buildingNamesSignal = computed(() => {
    const rooms = this.#roomBookingsSnapshotSignal();
    if (!rooms) {
      return [];
    }
    const filters = this.#bookingRequestFiltersSignal();
    const filtered = filters.siteNames?.length
      ? rooms.filter(room => filters.siteNames!.includes(room.siteName))
      : rooms;
    return Array.from(
      new Set(filtered.map(room => room.buildingName).filter(Boolean))
    ).sort();
  });

  readonly #floorNamesSignal = computed(() => {
    const rooms = this.#roomBookingsSnapshotSignal();
    if (!rooms) {
      return [];
    }
    const filters = this.#bookingRequestFiltersSignal();
    let filtered = rooms;
    if (filters.siteNames?.length) {
      filtered = filtered.filter(room =>
        filters.siteNames!.includes(room.siteName)
      );
    }
    if (filters.buildingNames?.length) {
      filtered = filtered.filter(room =>
        filters.buildingNames!.includes(room.buildingName)
      );
    }
    return Array.from(
      new Set(filtered.map(room => room.floorName).filter(Boolean))
    ).sort();
  });

  readonly #pageSignal = signal(0);
  readonly #pageSizeSignal = signal(20);
  readonly #lengthSignal = signal(0);
  readonly #sortBySignal = signal<BookingRequestSortField>('id');
  readonly #sortDirectionSignal = signal<Order>('desc');
  readonly #statusSignal = signal<BookingStatusApiResponseEnum | null>(null);
  readonly #pageParams = computed((): BookingRequestPageParams => {
    const filters = this.#bookingRequestFiltersSignal();
    const status = this.#statusSignal();
    return {
      page: this.#pageSignal(),
      pageSize: this.#pageSizeSignal(),
      sortBy: this.#sortBySignal(),
      sortDirection: this.#sortDirectionSignal(),
      ...(status && { status }),
      ...(filters.searchPhrase && { searchPhrase: filters.searchPhrase }),
      ...(filters.services?.length && {
        services: filters.services as unknown as RoomServicesModel[],
      }),
      ...(filters.siteNames?.length && { siteNames: filters.siteNames }),
      ...(filters.buildingNames?.length && {
        buildingNames: filters.buildingNames,
      }),
      ...(filters.floorNames?.length && { floorNames: filters.floorNames }),
    };
  });

  #availableServices: RoomServicesModel[] = [];
  #roleType: RoleType | null = null;

  get allBookings() {
    return this.#allBookingsSignal.asReadonly();
  }

  get bookingDetails() {
    return this.#bookingDetailsSignal.asReadonly();
  }

  get isDetailsDataLoading() {
    return this.#isDetailsDataLoadingSignal.asReadonly();
  }

  get isRoomBookingDataLoading() {
    return this.#isRoomBookingDataLoadingSignal.asReadonly();
  }

  get siteNames() {
    return this.#siteNamesSignal;
  }

  get buildingNames() {
    return this.#buildingNamesSignal;
  }

  get floorNames() {
    return this.#floorNamesSignal;
  }

  get page() {
    return this.#pageSignal;
  }

  get pageSize() {
    return this.#pageSizeSignal;
  }

  get length() {
    return this.#lengthSignal;
  }

  get sortBy() {
    return this.#sortBySignal;
  }

  get sortDirection() {
    return this.#sortDirectionSignal;
  }

  get status() {
    return this.#statusSignal.asReadonly();
  }

  public setStatus(status: BookingStatusApiResponseEnum | null) {
    this.#statusSignal.set(status);
    this.#pageSignal.set(0);
  }

  public setPage(pageIndex: number, pageSize: number) {
    this.#pageSignal.set(pageIndex);
    this.#pageSizeSignal.set(pageSize);
  }

  public resetPage() {
    this.#pageSignal.set(0);
  }

  public setSort(sortBy: BookingRequestSortField, sortDirection: Order) {
    this.#sortBySignal.set(sortBy);
    this.#sortDirectionSignal.set(sortDirection);
    this.#pageSignal.set(0);
  }

  public setBookingRequestFilters(filters: BookingRequestFilters) {
    this.#bookingRequestFiltersSignal.set(filters);
  }

  public clearBookingRequestFilters() {
    this.#bookingRequestFiltersSignal.set({
      searchPhrase: null,
    });
  }

  public getBookingRequestData(roomId: string, bookingId: number) {
    this.#bookingDetailsSignal.set(null);
    this.#isDetailsDataLoadingSignal.set(true);
    return forkJoin([
      this.#bookingRequestApiService.getBookingDetails(bookingId),
      this.#roomsApiService.getRoomDetails(roomId),
    ]).pipe(
      tap(([bookingRequest, roomDetails]) => {
        this.#bookingDetailsSignal.set({
          bookingDetails: bookingRequest,
          roomDetails: roomDetails,
        });
        this.#roomDetailsSignal.set(roomDetails);
      }),
      finalize(() => this.#isDetailsDataLoadingSignal.set(false))
    );
  }

  public getBookingHistory(bookingId: number) {
    return this.#bookingRequestApiService.getBookingHistory(bookingId);
  }

  public loadBookingHistory(bookingId: number) {
    this.#bookingHistorySignal.set([]);
    this.#isBookingHistoryLoadingSignal.set(true);
    return this.#bookingRequestApiService.getBookingHistory(bookingId).pipe(
      tap(history => this.#bookingHistorySignal.set(history)),
      finalize(() => this.#isBookingHistoryLoadingSignal.set(false))
    );
  }

  get bookingHistory() {
    return this.#bookingHistorySignal.asReadonly();
  }

  get isBookingHistoryLoading() {
    return this.#isBookingHistoryLoadingSignal.asReadonly();
  }

  public getRoomBookings(
    roleType: RoleType | null,
    services?: RoomServicesModel[]
  ): Observable<PagedResponse<RoomBookingModel>> {
    if (services) {
      this.#availableServices = services;
    }
    this.#roleType = roleType;
    if (this.#roleType) {
      this.#isRoomBookingDataLoadingSignal.set(true);
      return this.#bookingRequestApiService
        .getRoomBookings(this.#roleType, this.#pageParams())
        .pipe(
          tap(roomBookings => {
            this.#lengthSignal.set(roomBookings.totalSize);
            if (this.#availableServices.length > 0) {
              roomBookings.content.forEach(booking => {
                booking.servicesBadge = booking.services.map(service =>
                  this.serviceConverter(service)
                );
              });
            }
            this.#allBookingsSignal.set(roomBookings.content);
            this.#roomBookingsSnapshotSignal.set(roomBookings.content);
          }),
          finalize(() => this.#isRoomBookingDataLoadingSignal.set(false))
        );
    }
    return of({ content: [], totalSize: 0 });
  }

  public clearSearchQuery() {
    this.#bookingRequestFiltersSignal.set({ searchPhrase: null });
  }

  public approveBookingRequest(id: number, message: string) {
    this.#isDetailsDataLoadingSignal.set(true);
    return this.#bookingRequestApiService
      .approveRoomBooking({ id, message })
      .pipe(
        tap(bookingDetails => {
          this.#bookingDetailsSignal.set({
            bookingDetails,
            roomDetails: this.#roomDetailsSignal()!,
          });
          this.#googleAnalyticService.trackEvent(
            GoogleAnalyticsEventName.Approve,
            GoogleAnalyticsEventCategory.BookingRequest,
            `Request ID: ${id}`
          );
        }),
        finalize(() => this.#isDetailsDataLoadingSignal.set(false))
      );
  }

  public rejectBookingRequest(id: number, message: string) {
    this.#isDetailsDataLoadingSignal.set(true);
    return this.#bookingRequestApiService
      .rejectRoomBooking({ id, message })
      .pipe(
        tap(bookingDetails => {
          this.#bookingDetailsSignal.set({
            bookingDetails,
            roomDetails: this.#roomDetailsSignal()!,
          });
          this.#googleAnalyticService.trackEvent(
            GoogleAnalyticsEventName.Reject,
            GoogleAnalyticsEventCategory.BookingRequest,
            `Request ID: ${id}`
          );
        }),
        finalize(() => this.#isDetailsDataLoadingSignal.set(false))
      );
  }

  public cancelBookingRequest(id: number, message: string) {
    this.#isDetailsDataLoadingSignal.set(true);
    return this.#bookingRequestApiService
      .cancelRoomBooking({ id, message })
      .pipe(
        tap(bookingDetails => {
          this.#bookingDetailsSignal.set({
            bookingDetails,
            roomDetails: this.#roomDetailsSignal()!,
          });
          this.#googleAnalyticService.trackEvent(
            GoogleAnalyticsEventName.Cancel,
            GoogleAnalyticsEventCategory.BookingRequest,
            `Request ID: ${id}`
          );
        }),
        finalize(() => this.#isDetailsDataLoadingSignal.set(false))
      );
  }

  public updateBookingRequest(
    bookingRequest: BookingRequestDetailsUpdateModel
  ) {
    this.#isDetailsDataLoadingSignal.set(true);
    return this.#bookingRequestApiService
      .updateRoomBooking(bookingRequest)
      .pipe(
        tap(bookingDetails => {
          this.#bookingDetailsSignal.set({
            bookingDetails,
            roomDetails: this.#roomDetailsSignal()!,
          });
          this.#googleAnalyticService.trackEvent(
            GoogleAnalyticsEventName.EditAdmin,
            GoogleAnalyticsEventCategory.BookingRequest,
            `Request ID: ${bookingRequest.id}`
          );
        }),
        finalize(() => this.#isDetailsDataLoadingSignal.set(false))
      );
  }

  private serviceConverter(service: {
    id: number;
    serviceType: RoomServicesEnum;
    ticketName?: string;
    url?: string;
  }): {
    value: RoomServicesEnum;
    text: string;
    color: RdsBadgeColor;
    url?: string;
  } {
    const baseName = this.#availableServices
      .find(serviceItem => serviceItem.type === service.serviceType)!
      .name.replace('Service', '');

    if (service.serviceType === RoomServicesEnum.Furniture) {
      const hasTicket = !!service.ticketName;
      return {
        value: service.serviceType,
        text: hasTicket ? `${baseName} (${service.ticketName})` : baseName,
        color: hasTicket ? 'green' : 'red',
        url: service.url,
      };
    }

    return {
      value: service.serviceType,
      text: baseName,
      color: 'neutral',
      url: service.url,
    };
  }
}
